import React, { useState, useEffect, Suspense, lazy } from "react";
import Preloader from "../src/components/Pre";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { BrowserRouter as Router, Route, Switch, useLocation } from "react-router-dom";
import { SEO, SEOProvider } from "./components/SEO";
import { initMagnetic, initTilt, initCursorAura, initSpotlight, initParallax } from "./components/Effects/effects";
import { profile } from "./data/profile";
import { getResearchArticles } from "./data/researchArticles";
import ScrollProgress from "./components/ScrollProgress";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { SkeletonPage } from "./components/SkeletonLoader";
import { ToastProvider } from "./components/Toast";
import ScrollToTopButton from "./components/ScrollToTopButton";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
const Home = lazy(() => import("./components/Home/Home"));
const About = lazy(() => import("./components/About/About"));
const Projects = lazy(() => import("./components/Projects/Projects"));
const Resume = lazy(() => import("./components/Resume/Resume"));
const Certificates = lazy(() => import("./components/Certificates/Certificates"));
const Awards = lazy(() => import("./components/Certificates/Awards"));
const Volunteer = lazy(() => import("./components/Certificates/Volunteer"));
const Contact = lazy(() => import("./components/Contact"));
const Research = lazy(() => import("./components/Research"));
const Gallery = lazy(() => import("./components/Gallery"));
const Blog = lazy(() => import("./components/Blog"));

// Component to handle SEO for Research page with articles
function ResearchSEO() {
  const articles = getResearchArticles();

  return (
    <SEO
      title="Industry 4.0 & IoT Bibliometric Research (2004-2024) | Unais Ali | Research Publications"
      description="Two Decades of Industry 4.0 and IoT Research: bibliometric analysis of global trends, market dynamics, and technological convergences (2004–2024). Unais Ali et al., The American Journal of Engineering and Technology. Scopus, VOSviewer, Biblioshiny. Manufacturing, logistics, smart factories, cybersecurity, sustainable digital ecosystems. Cite this paper."
      url={`${profile.seo.url}/research`}
      canonical={`${profile.seo.url}/research`}
      keywords={[
        "Industry 4.0 bibliometric analysis",
        "IoT research trends 2004-2024",
        "bibliometric analysis Industry 4.0 IoT",
        "Unais Ali IoT paper",
        "Unais Ali Industry 4.0 research",
        "IoT global trends Scopus",
        "VOSviewer Industry 4.0",
        "Biblioshiny IoT",
        "smart manufacturing research",
        "industrial IoT market dynamics",
        "technological convergences IoT",
        "cite Industry 4.0 IoT paper",
        "American Journal of Engineering and Technology",
        "IoT research citations",
        "Unais Ali Research",
        "Research Publications",
        "AI Research",
        "Machine Learning Research",
        "IoT Research",
        "STEM Education Research",
        "EMU Research",
        "Academic Publications",
      ]}
      breadcrumbs={[
        { name: 'Home', item: `${profile.seo.url}/` },
        { name: 'Research', item: `${profile.seo.url}/research` },
      ]}
      articles={articles}
      pageType="CollectionPage"
    />
  );
}

// Component to handle AdSense initialization on route changes - DISABLED FOR NOW
// function AdSenseInitializer() {
//   const location = useLocation();
//
//   useEffect(() => {
//     // Initialize AdSense on route change for SPA compatibility
//     // This ensures AdSense works properly on all pages in a single-page application
//     try {
//       if (window.adsbygoogle) {
//         (window.adsbygoogle = window.adsbygoogle || []).push({});
//       }
//     } catch (e) {
//       console.error('AdSense initialization error:', e);
//     }
//   }, [location.pathname]);
//
//   return null;
// }

function App() {
  const [load, updateLoad] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      updateLoad(false);
    }, 1200);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!load) {
      initMagnetic(document);
      initTilt(document);
      initSpotlight(document);
      initParallax(document);
      initCursorAura();
      
      // Make toast function globally available for components that need it
      // This is a workaround until all components use the hook properly
      if (window.showToast === undefined) {
        // Will be set by ToastProvider
      }
    }
  }, [load]);

  return (
    <SEOProvider>
      <ToastProvider>
        <Router>
          {/* <AdSenseInitializer /> */}
          <Preloader load={load} />
          <a href="#main" className="skip-link">Skip to content</a>
          <div className="App" id={load ? "no-scroll" : "scroll"}>
            <div className="cursor-aura" aria-hidden="true" />
            <ScrollProgress />
            <Navbar />
            <ScrollToTop />
            <ScrollToTopButton />
          <SEO title={profile.seo.siteName} description={profile.seo.description} canonical={profile.seo.url} url={profile.seo.url} />
          <ErrorBoundary>
            <Suspense fallback={<SkeletonPage />}> 
              <Switch>
              <Route path="/" exact>
                <SEO
                  title="EdTech Founder, Graduate Student & STEM Education Leader | Empowering 20,000+ Students Globally"
                  description="Unais Ali - Founder & CEO of UTech Digital Education, empowering 20,000+ students in 50+ countries with AI, Robotics, and STEM education. MS Engineering Management graduate from Eastern Michigan University (4.0 GPA). PhD student in Cybersecurity and Applied Computing starting Fall 2026. Mentor and speaker. Developed 3 no-code AI tools tested in Michigan schools."
                  url={`${profile.seo.url}/`}
                  canonical={`${profile.seo.url}/`}
                  websiteSchema={true}
                  keywords={["Unais Ali", "EdTech Founder", "UTech Digital Education", "STEM Education Leader", "Graduate Student EMU", "20,000+ students", "50+ countries", "No-code AI tools", "Michigan schools"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                  ]}
                />
                <div key="/" className="route-fade"><Home /></div>
              </Route>
              <Route path="/project">
                <SEO
                  title="Engineering Projects: React/Node, PyTorch ML, IoT & Brainwave Control Systems"
                  description="Engineering projects by Unais Ali: React/Node platforms, PyTorch ML models, embedded systems, IoT solutions, and brainwave-controlled systems using OpenBCI. Includes UTech Digital Education platform, plant disease detection, smart agriculture monitoring, and mind-controlled technology projects."
                  url={`${profile.seo.url}/project`}
                  canonical={`${profile.seo.url}/project`}
                  keywords={["Engineering Projects", "React Node.js Projects", "PyTorch ML Models", "IoT Solutions", "OpenBCI", "Brainwave Control", "Embedded Systems", "UTech Platform", "Plant Disease Detection", "Smart Agriculture"]}
                  pageType="CollectionPage"
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Projects', item: `${profile.seo.url}/project` },
                  ]}
                />
                <div key="/project" className="route-fade"><Projects /></div>
              </Route>
              <Route path="/about">
                <SEO
                  title="About Unais Ali: EdTech Founder, Graduate Student & STEM Education Leader"
                  description="About Unais Ali - Founder & CEO of UTech Digital Education, empowering 20,000+ students in 50+ countries. MS Engineering Management graduate from Eastern Michigan University (4.0 GPA). PhD student in Cybersecurity and Applied Computing starting Fall 2026. Mentor and speaker. Developed 3 no-code AI tools tested in Michigan schools. F6S Top 100 ranking (#21), TRUEMU Certified, University Graduate Scholarship Award winner."
                  url={`${profile.seo.url}/about`}
                  canonical={`${profile.seo.url}/about`}
                  keywords={["About Unais Ali", "UTech Founder", "STEM Education Leader", "PhD Cybersecurity", "Applied Computing", "Engineering Management", "Student Mentor", "No-code AI Tools", "F6S Top 100", "TRUEMU", "EMU"]}
                  pageType="ProfilePage"
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'About', item: `${profile.seo.url}/about` },
                  ]}
                />
                <div key="/about" className="route-fade"><About /></div>
              </Route>
              <Route path="/resume">
                <SEO
                  title="Resume: EdTech Founder & Graduate Student | Experience, Education & Achievements"
                  description="Resume of Unais Ali - Founder & CEO of UTech Digital Education, MS Engineering Management graduate (EMU, 4.0 GPA), PhD student in Cybersecurity and Applied Computing starting Fall 2026. View professional experience, education, projects, achievements including F6S Top 100 ranking, TRUEMU certification, University Graduate Scholarship Award, and mentorship experience."
                  url={`${profile.seo.url}/resume`}
                  canonical={`${profile.seo.url}/resume`}
                  keywords={["Unais Ali Resume", "EdTech Founder Resume", "PhD Cybersecurity Resume", "Engineering Management Resume", "UTech CEO Resume", "EMU Graduate Resume", "STEM Education Resume"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Resume', item: `${profile.seo.url}/resume` },
                  ]}
                />
                <div key="/resume" className="route-fade"><Resume /></div>
              </Route>
              <Route path="/certificates">
                <SEO
                  title="Professional Certifications | AWS, Cisco, Python & TRUEMU Badge"
                  description="Professional certifications earned by Unais Ali: TRUEMU Professional Development Badge, AWS Fundamentals, Cisco cybersecurity and Python credentials, and Coursera specializations in machine learning and web development."
                  url={`${profile.seo.url}/certificates`}
                  canonical={`${profile.seo.url}/certificates`}
                  keywords={["Unais Ali Certifications", "TRUEMU Badge", "AWS Certifications", "Cisco Certifications", "Python Certifications", "Credly", "Coursera"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Honors & Awards', item: `${profile.seo.url}/awards` },
                    { name: 'Certificates', item: `${profile.seo.url}/certificates` },
                  ]}
                />
                <div key="/certificates" className="route-fade"><Certificates /></div>
              </Route>
              <Route path="/awards">
                <SEO
                  title="Awards & Recognitions | ASEE, EMU Scholarships, F6S Top 100 & Shell Tameer"
                  description="Awards and recognitions earned by Unais Ali: ASEE Engineering Educator Grant, Thank-An-Eagle, university scholarships, F6S Top 100 ranking, Shell Tameer Outstanding Pitch, and EMU mini-grants."
                  url={`${profile.seo.url}/awards`}
                  canonical={`${profile.seo.url}/awards`}
                  keywords={["Unais Ali Awards", "ASEE Grant", "Thank-An-Eagle", "F6S Top 100", "University Graduate Scholarship", "Shell Tameer", "EMU Awards"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Honors & Awards', item: `${profile.seo.url}/awards` },
                    { name: 'Awards', item: `${profile.seo.url}/awards` },
                  ]}
                />
                <div key="/awards" className="route-fade"><Awards /></div>
              </Route>
              <Route path="/volunteer">
                <SEO
                  title="Volunteer Experience | EMUiNVENT, NED Scholars, Ford Field & NED University"
                  description="Volunteer work by Unais Ali: EMUiNVENT education mentor, Manufacturing in America exhibitor at Ford Field, NED Scholars marketing advisor, IDEA Tank guest speaker, and Thar Foundation capacity building team lead."
                  url={`${profile.seo.url}/volunteer`}
                  canonical={`${profile.seo.url}/volunteer`}
                  keywords={["Unais Ali Volunteer", "EMUiNVENT Mentor", "NED Scholars", "Ford Field Exhibitor", "ASEE Moderator", "STEM Mentorship"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Honors & Awards', item: `${profile.seo.url}/awards` },
                    { name: 'Volunteer', item: `${profile.seo.url}/volunteer` },
                  ]}
                />
                <div key="/volunteer" className="route-fade"><Volunteer /></div>
              </Route>
              <Route path="/contact">
                <SEO
                  title="Contact Unais Ali: EdTech Founder & Graduate Student | Get in Touch"
                  description="Contact Unais Ali - Founder & CEO of UTech Digital Education, Graduate Student at Eastern Michigan University. Get in touch via email or LinkedIn for opportunities, collaborations, mentorship, or questions about STEM education, scholarships, and study abroad guidance."
                  url={`${profile.seo.url}/contact`}
                  canonical={`${profile.seo.url}/contact`}
                  keywords={["Contact Unais Ali", "UTech Contact", "EdTech Founder Contact", "Graduate Student Contact", "Mentorship", "Collaboration", "STEM Education Contact"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Contact', item: `${profile.seo.url}/contact` },
                  ]}
                />
                <div key="/contact" className="route-fade"><Contact /></div>
              </Route>
              <Route path="/research">
                <ResearchSEO />
                <div key="/research" className="route-fade"><Research /></div>
              </Route>
              <Route path="/gallery">
                <SEO
                  title="Photo Gallery: STEM Exhibitions, Events & Achievements | Unais Ali"
                  description="Browse the photo gallery showcasing STEM exhibitions, events, achievements, and memories from Unais Ali's journey as EdTech Founder, Graduate Student, and STEM Education Leader. Includes images from STEM Science Festival, Dare2Design competitions, EMU events, and UTech activities."
                  url={`${profile.seo.url}/gallery`}
                  canonical={`${profile.seo.url}/gallery`}
                  keywords={["Unais Ali Gallery", "STEM Exhibition Photos", "STEM Science Festival", "Dare2Design", "EMU Events", "UTech Gallery", "Achievement Photos", "Portfolio Images"]}
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Gallery', item: `${profile.seo.url}/gallery` },
                  ]}
                />
                <div key="/gallery" className="route-fade"><Gallery /></div>
              </Route>
              <Route path="/blog">
                <SEO
                  title="Blog & Articles: STEM Education, Scholarships, Study Abroad & EdTech Insights"
                  description="Read articles and insights by Unais Ali on STEM education, scholarship applications, SOP writing, F1 visa processes, study abroad guidance, EdTech innovation, student mentorship, and experiences from building UTech Digital Education and pursuing graduate studies at Eastern Michigan University."
                  url={`${profile.seo.url}/blog`}
                  canonical={`${profile.seo.url}/blog`}
                  keywords={["Unais Ali Blog", "STEM Education Blog", "Scholarship Guide", "SOP Writing", "F1 Visa", "Study Abroad", "EdTech Blog", "Student Mentorship", "EMU Blog", "Graduate Student Blog"]}
                  pageType="CollectionPage"
                  breadcrumbs={[
                    { name: 'Home', item: `${profile.seo.url}/` },
                    { name: 'Blog', item: `${profile.seo.url}/blog` },
                  ]}
                />
                <div key="/blog" className="route-fade"><Blog /></div>
              </Route>
              </Switch>
            </Suspense>
            </ErrorBoundary>
            <Footer />
          </div>
        </Router>
      </ToastProvider>
    </SEOProvider>
  );
}

export default App;
